import { READINESS_FUNNEL_CONTEXT } from "~/lib/shared/readiness/funnel";

import {
  TrackingAction,
  type TrackingCampaign,
  type TrackingMedium,
} from "./tracking-parameters";

/**
 * Central analytics event tracking utility.
 *
 * Sends supplementary interactions through the Google tag API, with a GTM
 * custom-event fallback. Durable milestones are recorded by server boundaries;
 * browser events are useful for UX analysis but are not the funnel authority.
 * All event names use snake_case for consistency.
 *
 * Aliases: analytics, event tracking, gtag helper
 */

// ---------------------------------------------------------------------------
// Core helper
// ---------------------------------------------------------------------------

function trackEvent(
  eventName: string,
  params: Record<string, unknown> = {},
): void {
  if (typeof window === "undefined") {
    return;
  }

  // The installed Google tag handles named events and their parameters.
  // Do not also push a custom event: a GTM forwarding rule could count it twice.
  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, params);
    return;
  }
  window.dataLayer?.push({ event: eventName, ...params });
}

/** Preserve the campaign dimensions used by existing reports. */
export function trackCampaignClick(
  track: { campaign: TrackingCampaign; medium: TrackingMedium },
  label: string,
): void {
  trackEvent("event", {
    eventCampaign: track.campaign,
    eventAction: TrackingAction.Click,
    eventMedium: track.medium,
    eventLabel: label,
  });
}

// ---------------------------------------------------------------------------
// 1. Coach profile viewed
// ---------------------------------------------------------------------------

export function trackCoachProfileViewed(
  username: string,
  source?: string,
): void {
  trackEvent("coach_profile_viewed", {
    coach_username: username,
    ...(source && { source }),
  });
}

// ---------------------------------------------------------------------------
// 2. CTA button clicked (Book a call / Send message / custom CTA text)
// ---------------------------------------------------------------------------

export function trackCtaButtonClicked(params: {
  button_text: string;
  destination: string;
  coach_username: string;
  offer_id: string;
  offer_type: string;
}): void {
  trackEvent("cta_button_clicked", params);
}

// ---------------------------------------------------------------------------
// 3. Wheel of Life
// ---------------------------------------------------------------------------

/** Which Wheel page the visitor finished: the platform's or a coach's own. */
export type WheelSurface = "public" | "coach";

/** Sent once the Wheel is saved. The database count covers every visitor. */
export function trackWheelCompleted(surface: WheelSurface): void {
  trackEvent("wheel_completed", { surface });
}

/** The results page's "Find your next step" button, which opens the intake. */
export function trackWheelNextStepClicked(): void {
  trackEvent("wheel_next_step_clicked");
}

// ---------------------------------------------------------------------------
// 4. Booking page viewed
// ---------------------------------------------------------------------------

export function trackBookingPageViewed(username: string): void {
  trackEvent("booking_page_viewed", { coach_username: username });
}

// ---------------------------------------------------------------------------
// 5. Message sent to coach
// ---------------------------------------------------------------------------

export function trackMessageSent(coachUsername: string): void {
  trackEvent("message_sent", { coach_username: coachUsername });
}

// ---------------------------------------------------------------------------
// 6. Discovery flow step completed
// ---------------------------------------------------------------------------

export function trackDiscoveryStepCompleted(params: {
  step_number: number;
  step_name: string;
}): void {
  trackEvent("discovery_step_completed", params);
}

// ---------------------------------------------------------------------------
// 7. Intake flow step completed
// ---------------------------------------------------------------------------

export function trackIntakeStepCompleted(params: {
  step_index: number;
  step_id: string;
  step_type: string;
}): void {
  trackEvent("intake_step_completed", params);
}

// ---------------------------------------------------------------------------
// 8. 6-Figure Readiness Score
// ---------------------------------------------------------------------------

/** Event names keep their `diagnose_` prefix: dashboards key on them. */
export type ReadinessSurface = "public" | "signed_in";

function trackReadinessEvent(
  event: string,
  params: Record<string, unknown>,
): void {
  trackEvent(event, { ...READINESS_FUNNEL_CONTEXT, ...params });
}

export function trackReadinessViewed(surface: ReadinessSurface): void {
  trackReadinessEvent("diagnose_page_viewed", { surface });
}

export function trackReadinessStarted(surface: ReadinessSurface): void {
  trackReadinessEvent("diagnose_started", { surface });
}

export function trackReadinessQuestionViewed(params: {
  surface: ReadinessSurface;
  question_number: number;
  question_id: string;
}): void {
  trackReadinessEvent("diagnose_question_viewed", params);
}

export function trackReadinessQuestionCompleted(params: {
  surface: ReadinessSurface;
  question_number: number;
  question_id: string;
}): void {
  trackReadinessEvent("diagnose_question_completed", params);
}

export function trackReadinessResultRevealed(params: {
  surface: ReadinessSurface;
  band: string | null;
  holding_back: string | null;
}): void {
  trackReadinessEvent("diagnose_result_revealed", params);
}

export function trackReadinessEmailSubmitted(): void {
  trackReadinessEvent("diagnose_email_submitted", { surface: "public" });
}

// ---------------------------------------------------------------------------
// 9. Public site navigation
// ---------------------------------------------------------------------------

export type NavClickLocation = "header" | "mobile_menu" | "footer";

export function trackNavClick(params: {
  location: NavClickLocation;
  label: string;
  destination: string;
}): void {
  trackEvent("nav_click", params);
}

// ---------------------------------------------------------------------------
// 10. Coach lead follow-up
// ---------------------------------------------------------------------------

/** Lead follow-up events carry no lead, name or email: only which action. */
export function trackLeadReplyCopied(): void {
  trackEvent("lead_reply_copied");
}

export function trackLeadReplyOpened(): void {
  trackEvent("lead_reply_opened");
}

export function trackLeadFollowupsCopied(params: { message_id: string }): void {
  trackEvent("lead_followups_copied", params);
}

export function trackLeadStatusChanged(params: { status: string }): void {
  trackEvent("lead_status_changed", params);
}
